import auth from '@/middleware/modules/auth'
import inviteUsers from '@/middleware/modules/invite-users'
import inviteTrafficManager from '@/middleware/modules/invite-traffic-manager'

import viewAccountUsers from '@/middleware/modules/view-account-users'

import store from '@/store/'
import { resolveEntityAudience } from '@/mixins/entity_naming'

// Router guards run before any component mounts, so there's no `this.$isAgency()`
// available — this mirrors the same check from plugins/roleAndAccess.js directly
// against the store, same as resolveEntityAudience's other caller (the
// entityAudience computed in entity_naming.js).
function isAgencyFromStore() {
  const userType = store.getters['user/type']
  const parentType = store.state.user.parent_user

  if (['employee', 'agency-employee', 'brand-employee'].includes(userType)) {
    return parentType && parentType.slug === 'agency'
  }

  return userType === 'agency'
}

function entityAudienceFromStore() {
  return resolveEntityAudience(store.getters['user/type'], isAgencyFromStore)
}

// SRR-2580/SRR-2581 QA: Traffic Manager's "Customers" entity and Brand
// Admin's "Brand employees" entity each need their own URL rather than
// sharing /account/brands or /account/account-users. Each pair of routes
// below shares one component; `belongsHere` decides which side of the pair
// the current audience belongs on, and redirects to `siblingRouteName` when
// they land on the wrong one directly (bookmark, typed URL, stale link) —
// so the URL always matches the audience actually viewing it.
function redirectUnlessAudience(belongsHere, siblingRouteName) {
  return (to, from, next) => {
    if (belongsHere(entityAudienceFromStore())) {
      next()
    } else {
      next({ name: siblingRouteName, query: to.query })
    }
  }
}

const Account = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/Account.vue')
const AccountSettings = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/Settings.vue')
const AccountPassword = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/Password.vue')
const AccountSocial = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/SocialAccount.vue')
const AccountSubscription = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/Subscription.vue')
const AccountBillingHistory = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/BillingHistory.vue')
const AccountSocialShare = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/SocialShare.vue')
const AccountMoreSettings = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/MoreSettings.vue')
const AccountApiPublic = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/APIPublic.vue')
const AccountAuthorisedApps = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/AuthorisedApps.vue')
const AccountLimits = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/AccountLimits.vue')
const AccountUsers = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/AccountUsers.vue')
const AccountClients = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/AccountClients.vue')
const TrafficManager = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/TrafficManager.vue')
const SyncBrands = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/SyncBrands.vue')
const Inventory = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/Inventory.vue')
const AccountAgreements = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/Agreements.vue')
const AccountNotifications = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/Notifications.vue')
const WebhooksSettings = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/Webhooks.vue')
const WhitelabelConfiguration = () => import(/* webpackChunkName: "_v-account" */ '@/views/account/WhitelabelConfiguration.vue')

export default [
  {
    path: 'account',
    name: 'account',
    component: Account,
    redirect: { name: 'settings' },
    meta: {
      layout: 'app-layout',
      middleware: [auth],
    },

    children: [
      {
        path: 'settings',
        name: 'settings',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountSettings,
      },
      {
        path: 'password',
        name: 'password',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountPassword,
      },
      {
        path: 'social',
        name: 'social',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountSocial,
      },
      {
        path: 'subscription',
        name: 'account-subscription',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountSubscription,
      },
      {
        path: 'history',
        name: 'history',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountBillingHistory,
      },
      {
        path: 'social-share',
        name: 'social-share',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountSocialShare,
      },
      {
        path: 'more-settings',
        name: 'more-settings',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountMoreSettings,
      },
      {
        path: 'api-public',
        name: 'api-public',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountApiPublic,
      },
      {
        path: 'authorised-apps',
        name: 'authorised-apps',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountAuthorisedApps,
      },
      {
        path: 'agreements',
        name: 'agreements',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountAgreements,
      },
      {
        path: 'account-limits',
        name: 'account-limits',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountLimits,
      },
      {
        path: 'account-users',
        name: 'account-users',
        beforeEnter: redirectUnlessAudience((audience) => audience !== 'brand', 'account-brand-employees'),
        meta: {
          layout: 'app-layout',
          middleware: [auth, inviteUsers],
        },
        component: AccountUsers,
      },
      {
        path: 'brand-employees',
        name: 'account-brand-employees',
        beforeEnter: redirectUnlessAudience((audience) => audience === 'brand', 'account-users'),
        meta: {
          layout: 'app-layout',
          middleware: [auth, inviteUsers],
        },
        component: AccountUsers,
      },
      {
        path: 'brands',
        name: 'account-clients',
        beforeEnter: redirectUnlessAudience((audience) => audience !== 'traffic-manager', 'account-customers'),
        meta: {
          layout: 'app-layout',
          middleware: [auth, inviteUsers, viewAccountUsers],
        },
        component: AccountClients,
      },
      {
        path: 'customers',
        name: 'account-customers',
        beforeEnter: redirectUnlessAudience((audience) => audience === 'traffic-manager', 'account-clients'),
        meta: {
          layout: 'app-layout',
          middleware: [auth, inviteUsers, viewAccountUsers],
        },
        component: AccountClients,
      },
      {
        path: 'traffic-managers',
        name: 'traffic-managers',
        meta: {
          layout: 'app-layout',
          middleware: [auth, inviteTrafficManager],
        },
        component: TrafficManager,
      },
      {
        path: 'sync-brands',
        name: 'sync-brands',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: SyncBrands,
      },
      {
        path: 'inventory',
        name: 'inventory',
        meta: {
          layout: 'app-layout',
          middleware: [auth, inviteTrafficManager],
        },
        component: Inventory,
      },
      {
        path: 'notifications',
        name: 'notifications',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: AccountNotifications,
      },
      {
        path: 'webhooks',
        name: 'webhooks',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: WebhooksSettings,
      },
      {
        path: 'whitelabel-configuration',
        name: 'whitelabel-configuration',
        meta: {
          layout: 'app-layout',
          middleware: [auth],
        },
        component: WhitelabelConfiguration,
      },
    ],
  },
]
